import type { FC } from 'react';
import { Link } from '@/base/router';
import { useSettings } from '@/settings/api';
import { Coin } from '@/shared/Coin';
import { Digits } from '@/shared/Digits';
import highVolumeIcon from '../assets/high-volume.svg';
import mostPopularIcon from '../assets/most-popular.svg';
import topGainerIcon from '../assets/top-gainer.svg';
import type { CryptocurrencyList, QuoteCurrency } from './types';

export const CryptocurrencyCard: FC<{
  list: CryptocurrencyList;
  quote: QuoteCurrency;
  isFirstCard?: boolean;
}> = ({ list, quote, isFirstCard = false }) => {
  const [{ language }] = useSettings();

  const getIcon = () => {
    switch (list.icon) {
      case 'up':
        return (
          <img
            src={topGainerIcon}
            alt="Top Gainer"
            className="w-full h-full text-content-brand"
          />
        );
      case 'flame':
        return (
          <img
            src={highVolumeIcon}
            alt="High Volume"
            className="w-full h-full text-content-brand"
          />
        );
      case 'star':
        return (
          <img
            src={mostPopularIcon}
            alt="Most Popular"
            className="w-full h-full text-content-brand"
          />
        );
    }
  };

  return (
    <div className="bg-background-primary rounded-2xl border border-border-disabled flex flex-col shadow-background-hover">
      <div className="flex items-center gap-2 mb-5 p-5 pb-0">
        <div className="w-[40px] h-[40px]">{getIcon()}</div>
        <div>
          <h4 className="text-base font-medium leading-[18px] mb-0">
            {list.title}
          </h4>
          <p className="text-xs text-content-tertiary leading-[18px]">
            {list.subtitle}
          </p>
        </div>
      </div>

      <div className="border-t border-border-disabled flex-1 flex flex-col">
        {list.items.map((item, index) => (
          <Link
            key={item.name}
            to={`/${language}/buy-and-sell/${item.slug}`}
            className="flex items-center justify-between hover:bg-background-disabled px-5 py-3 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Coin
                variant="icon"
                coin={item.symbol}
                size={8}
                priority={isFirstCard && index < 2}
              />
              <div className="min-w-0">
                <p className="text-sm capitalize truncate">
                  <span className="font-medium">{item.symbol}</span>{' '}
                  <span className="text-content-tertiary">({item.name})</span>
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0 ml-2">
              <Digits
                value={item.price}
                coin={quote}
                className="text-sm text-content-primary whitespace-nowrap"
              />
              <Digits
                value={item.change.toFixed(2)}
                unit="%"
                color="auto"
                sign="always"
              />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};
